import { DI_TOKENS, resolve } from '@src/di/container';
import { renameFile } from '@src/services/upload';
import { uploadAudioSidecar } from '@src/services/upload/sidecar';
import { uploadCredentialsService } from '@src/services/UploadCredentialsService';
import { LogManager } from '@src/utility/Logger';
import { v4 as uuid } from 'uuid';

import type { PutSidecarObject } from '@src/services/upload/sidecar';
import type { ZoomRecordingFile } from '@src/services/ZoomService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { UserIdentityStore } from '@src/stores-v2/UserIdentityStore';
import type { Showcase } from '@src/types.ts/story';

// S3 key generator for external uploads (moved from UploadService)
function getExternalUploadsBucketPathKey(
  fileKey: string,
  organization_id: string | null | undefined,
): string {
  return `externalUploads/${organization_id || 'no_org'}/${fileKey}`;
}
export type StoryVideoMusicStrategy = 'keep' | 'process';

export interface CreateBlankStoryParams {
  storyType: 'raw' | 'produced' | null;
  storyTellerName: string;
  storyTellerTitle: string;
  /** Filename-derived name shown until the wizard's Save renames the story. */
  initialTitle?: string;
  organization?: Showcase;
  userData?: { id: string; name: string; email: string; type: string } | null;
}

export interface StartProcessingParams {
  storyId: string;
  storyName: string;
  storyType: 'raw' | 'produced';
  storyVideoUrl?: string | null;
  isUrlUpload: boolean;
  isMultiFileUpload: boolean;
  multiFileVideoUrls?: string[] | null;
  storyVideoFile?: File | null;
  organizationId?: string;
  videoMusicStrategy: StoryVideoMusicStrategy;
  /**
   * PLAT-11697: S3 key of the Audio Sidecar that has already landed. Sent only
   * when processing starts on the Sidecar (single local file, Original still
   * uploading); the server then starts the execution from it. Never sent for
   * URL, Zoom or multi-file uploads.
   */
  audioSidecarKey?: string;
  tracker?: {
    transactionId: string | null;
    analyticsContext: Record<string, unknown>;
    analyticsLog: (...args: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
  transcriptionProviders: string[];
  zoomRecordingFiles?: ZoomRecordingFile[];
  zoomPersonId?: string;
  zoomEmail?: string;
  zoomPassword?: string;
}

export interface VideoUploadProgressCallbacks {
  onProgress: (percent: number, done?: boolean) => void;
  onUploaded: () => void;
  onError: (err: unknown) => void;
}

export interface MultiFileUploadResult {
  uploadedUrls: string[];
}

export type SidecarUploadResult =
  | { ok: true; key: string; wallMs: number }
  | { ok: false; reason: 'no-organization' | 'credentials' | 'upload-failed' };

export interface StoryCreationServiceDeps {
  videoCreator: VideoCreatorStore | IVideoCreatorStore;
  userIdentity: UserIdentityStore;
}

const logger = LogManager.getLogger('service:StoryCreationService');

export class StoryCreationService {
  private videoCreator: VideoCreatorStore | IVideoCreatorStore;
  private userIdentity: UserIdentityStore;

  constructor(deps: StoryCreationServiceDeps) {
    this.videoCreator = deps.videoCreator;
    this.userIdentity = deps.userIdentity;
  }

  formatCurrentUserData() {
    const userData = this.userIdentity.getUserIdentity?.();
    if (!userData) return null;
    const { id, name, email, type } = userData;
    return { id, name, email, type };
  }

  async createBlankStory(params: CreateBlankStoryParams) {
    logger.info('createBlankStory', {
      type: params.storyType,
    });

    const blank = await this.videoCreator.createStory({
      // The uuid form is a last resort — an unnamed story surfaces in Story
      // Manager rows and stopwatches the moment it exists.
      title: params.initialTitle || `user-upload-${uuid()}`,
      storyType: params.storyType || undefined,
      storyTeller: {
        name: params.storyTellerName,
        id: (
          await this.videoCreator.upsertStoryTeller({
            name: params.storyTellerName,
            title: params.storyTellerTitle,
          })
        ).id,
      },
      // transitional: accessing legacy organization shape on videoCreator

      primaryShowcase: params.organization,
      externalUploadLog: { uploadedBy: params.userData },
      byExternalUser: true,
    });
    logger.info('blank-story-created', {
      storyId: blank.id,
      storyType: params.storyType,
    });
    return blank;
  }

  async startOldVideoProcessingPipeline(
    storyId: string,
    storyVideoFile?: File | null,
  ) {
    try {
      if (!storyVideoFile) return;
      const fileName = renameFile(storyVideoFile, storyId).name;

      const orgId = this.videoCreator.organization?.id;
      const s3Key = getExternalUploadsBucketPathKey(fileName, orgId);
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task-old`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storyId, s3Key }),
        },
      );
      if (!resp.ok) throw new Error(`old-processing-not-ok ${resp.status}`);
    } catch (err) {
      logger.warn('old-processing-pipeline-error suppressed', { err });
    }
  }

  async startProcessingUploadedVideo(params: StartProcessingParams) {
    const {
      storyId,
      storyName,
      storyType,
      isUrlUpload,
      isMultiFileUpload,
      multiFileVideoUrls,
      storyVideoUrl,
      storyVideoFile,
      organizationId,
      videoMusicStrategy,
      audioSidecarKey,
      tracker,
      transcriptionProviders,
      zoomRecordingFiles,
      zoomPersonId,
      zoomEmail,
      zoomPassword,
    } = params;

    logger.info('start-processing', {
      storyId,
      storyType,
      isUrlUpload,
      isMultiFileUpload,
      videoMusicStrategy,
      audioPath: audioSidecarKey ? 'sidecar' : 'server-extract',
    });

    try {
      const requestBody: Record<string, unknown> = {
        transactionId: tracker?.transactionId,
        analyticsContext: {
          ...(tracker?.analyticsContext || {}),
          story: { id: storyId, name: storyName },
        },
        storyId,
        showcaseId: organizationId,
        userData: this.formatCurrentUserData(),
        audioProcessPreset:
          videoMusicStrategy === 'process'
            ? storyType === 'produced'
              ? 'REMOVE_MUSIC'
              : 'CORRECT_AUDIO'
            : undefined,
        transcriptionProviders,
      };

      if (isUrlUpload && zoomRecordingFiles) {
        requestBody.zoomRecordingFiles = zoomRecordingFiles;
        requestBody.zoomPersonId = zoomPersonId;
        requestBody.zoomEmail = zoomEmail;
        requestBody.zoomPassword = zoomPassword;
        requestBody.s3Key = `sessions/${storyId}/videos/original.mp4`;
      } else if (isUrlUpload && storyVideoUrl) {
        // Guard: never send the Zoom sentinel URL as a real sourceUrl
        if (storyVideoUrl.startsWith('zoom-import://')) {
          throw new Error(
            'Zoom recording metadata missing — cannot fall back to URL upload',
          );
        }
        requestBody.sourceUrl = storyVideoUrl;
        requestBody.s3Key = `sessions/${storyId}/videos/original.mp4`;
      } else if (isMultiFileUpload && multiFileVideoUrls) {
        requestBody.videoUrls = multiFileVideoUrls;
        requestBody.isMultiFileConcat = true;
      } else if (storyVideoFile) {
        requestBody.s3Key = getExternalUploadsBucketPathKey(
          renameFile(storyVideoFile, storyId).name,
          organizationId,
        );
        // Sidecar Path: the execution starts from the Sidecar while the
        // Original (s3Key above) is still uploading.
        if (audioSidecarKey) requestBody.audioSidecarKey = audioSidecarKey;
      }

      tracker?.analyticsLog('info', 'Video processing started');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
      );
      logger.info('processing-api-response', {
        storyId,
        status: response.status,
        ok: response.ok,
      });
      if (!response.ok) throw new Error(`processing-not-ok ${response.status}`);
    } catch (error) {
      logger.error('processing-api-failed', { storyId, error });
      tracker?.analyticsLog?.('error', 'Error calling video processing API', {
        error,
      });
      throw error;
    }
  }

  async uploadMultiFiles(
    files: File[],
    storyId: string,
    organizationId: string | undefined,
    clerkToken: string | undefined,
    callbacks: VideoUploadProgressCallbacks,
    onMechanismResolved?: (m: 'worker' | 'main-thread') => void,
    signal?: AbortSignal,
  ): Promise<MultiFileUploadResult> {
    const totalSizeBytes = files.reduce((sum, f) => sum + f.size, 0);
    logger.info('upload-multi-files-start', {
      storyId,
      fileCount: files.length,
      totalSizeBytes,
      hasToken: !!clerkToken,
    });
    const uploadService = resolve(DI_TOKENS.UPLOAD_SERVICE);
    if (!uploadService) throw new Error('UploadService not initialized');
    // Upload all files in parallel tracking aggregate progress
    const progressMap = new Map<number, number>();
    files.forEach((_, idx) => progressMap.set(idx, 0));

    const computeOverall = () => {
      const total = Array.from(progressMap.values()).reduce((s, v) => s + v, 0);
      return total / files.length; // raw 0-100 overall percent
    };

    const uploadPromises = files.map(async (file, i) => {
      const key = getExternalUploadsBucketPathKey(
        `${storyId}_part_${i}`,
        organizationId,
      );
      const org = this.videoCreator.organization?.organization;
      await uploadService.uploadFileToS3ExternalUploads(file, key, org, {
        onUploadProgress: (p: number, done?: boolean) => {
          progressMap.set(i, p);
          callbacks.onProgress(Math.min(100, computeOverall()), done);
        },
        ...(clerkToken ? { authToken: clerkToken } : {}),
        signal,
        onResolvedMechanism: m => {
          try {
            onMechanismResolved?.(m);
          } catch {
            /* ignore */
          }
        },
      });
      return `https://${process.env.REACT_APP_EXTERNAL_UPLOADS_BUCKET}.s3.amazonaws.com/${key}`; // heuristic
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    callbacks.onProgress(100, true);
    callbacks.onUploaded();
    return { uploadedUrls };
  }

  /**
   * PLAT-11697: PUT the Audio Sidecar next to the Original, with the same STS
   * credentials and `keyPrefix` the video upload uses. Silent on failure —
   * the caller falls back to the Server-Extract Path.
   */
  async uploadAudioSidecar(
    blob: Blob,
    storyId: string,
    organizationId: string | null | undefined,
    clerkToken: string | undefined,
    options: { put?: PutSidecarObject } = {},
  ): Promise<SidecarUploadResult> {
    if (!organizationId) return { ok: false, reason: 'no-organization' };
    const credentials = await uploadCredentialsService.fetch(
      organizationId,
      clerkToken,
    );
    if (!credentials.ok) {
      logger.warn('sidecar-credentials-failed', {
        storyId,
        errorKind: credentials.error.kind,
        status: credentials.error.status,
      });
      return { ok: false, reason: 'credentials' };
    }
    // Beside the Original: the Original's key is built client-side from the
    // Dato organization id (getExternalUploadsBucketPathKey), while the
    // credentials keyPrefix is derived from the Clerk active org — a different
    // id space. upload-task validates the Sidecar against the Original's prefix.
    const key = getExternalUploadsBucketPathKey(
      `${storyId}.audio.m4a`,
      organizationId,
    );
    logger.info('sidecar-upload-start', { storyId, key, bytes: blob.size });
    const outcome = await uploadAudioSidecar(
      blob,
      {
        bucket: credentials.value.bucket,
        region: credentials.value.region,
        key,
        credentials: credentials.value.credentials,
      },
      { put: options.put },
    );
    if (!outcome.ok) {
      logger.warn('sidecar-upload-failed', {
        storyId,
        key,
        attempts: outcome.attempts,
      });
      return { ok: false, reason: 'upload-failed' };
    }
    logger.info('sidecar-upload-complete', {
      storyId,
      key,
      wallMs: outcome.wallMs,
      attempts: outcome.attempts,
    });
    return { ok: true, key, wallMs: outcome.wallMs };
  }

  async uploadSingleFile(
    file: File,
    storyId: string,
    organization: string | null | undefined, // transitional narrowed
    clerkToken: string | undefined,
    callbacks: VideoUploadProgressCallbacks,
    onMechanismResolved?: (m: 'worker' | 'main-thread') => void,
    signal?: AbortSignal,
  ): Promise<void> {
    logger.info('upload-single-file-start', {
      storyId,
      fileName: file.name,
      fileSizeBytes: file.size,
      hasToken: !!clerkToken,
    });
    const uploadService = resolve(DI_TOKENS.UPLOAD_SERVICE);
    if (!uploadService) throw new Error('UploadService not initialized');
    const renamed = renameFile(file, storyId);
    const key = getExternalUploadsBucketPathKey(renamed.name, organization);
    await uploadService.uploadFileToS3ExternalUploads(
      renamed,
      key,
      organization,
      {
        onUploadProgress: (progress: number, done?: boolean) => {
          callbacks.onProgress(Math.min(100, progress), done);
          if (done) callbacks.onUploaded();
        },
        ...(clerkToken ? { authToken: clerkToken } : {}),
        signal,
        onResolvedMechanism: m => {
          try {
            onMechanismResolved?.(m);
          } catch {
            /* ignore */
          }
        },
      },
    );
  }

  // NOTE: pollStoryUpdates removed in initial extraction (hook still owns for now)
}

export type IStoryCreationService = StoryCreationService; // simple alias for DI for now
